import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import Axios, { AxiosResponse } from 'axios';
import { v4 as uuid } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { refreshTokenWithLock as refreshToken } from '../utils/awsCognito';
import { getIdToken } from '../utils/authTokenStorage';
import i18n from '../utils/i18n';
import { config as globalConfig } from '../assets/config';
import { useAppDispatch } from '../store/hooks';
import { setUnauthorized, signOut } from '../store/authSlice';
import { setMessage, type MessageType } from '../store/messageSlice';

type WebAPIContextSchema = {
  request: (payload: any) => Promise<any>,
  requestBackground: (payload: { query: string; variables?: Record<string, any> }) => Promise<any>,
  loading: boolean,
  setLoading: (loading: boolean) => void
}

export const WebAPIContext = createContext({
  request: async () => {},
  requestBackground: async () => {},
  loading: false,
  setLoading: () => {}
} as WebAPIContextSchema);

/** エラーオブジェクトからUI表示用の安全な詳細文字列を生成する */
const getSafeErrorDetail = (error: unknown): string => {
  // AxiosError: message + ステータスコードのみ（config/stack/response bodyは含めない）
  if (Axios.isAxiosError(error)) {
    const status = error.response?.status ? ` (${error.response.status})` : '';
    return `${error.message}${status}`;
  }
  // GraphQLエラー（interceptor経由: response.data.errorsが存在する場合）
  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors: Array<{ message?: string }> }).errors;
    return errors?.[0]?.message || '';
  }
  // その他のエラー
  return error instanceof Error ? error.message : '';
};

let isInitialized = false;

export const WebAPIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const webClient = Axios.create({
    baseURL: globalConfig.appSyncUrl,
    headers: {
      'Content-Type': 'application/graphql',
    },
    timeout: 30000, // 30秒
  });

  const [loading, setLoadingState] = useState(false);
  // requestMapはレンダー間で永続化する必要があるためuseRefで保持（旧実装では毎render再生成される潜在バグがあった）
  const requestMapRef = useRef<Map<string, string>>(new Map());
  // 手動 setLoading(true) の状態を requestMap とは独立に保持することで、
  // request の finally で自動リセットされないようにする
  const manualLoadingRef = useRef<boolean>(false);

  /** 手動フラグと実行中リクエストのORで実loading状態を更新する */
  const updateLoading = () => {
    setLoadingState(requestMapRef.current.size > 0 || manualLoadingRef.current);
  };

  /** 外部公開用 setLoading: 手動フラグを更新して実loading状態に反映 */
  const setLoading = (v: boolean) => {
    manualLoadingRef.current = v;
    updateLoading();
  };

  // loading中はブラウザの閉じる/リロード/URL変更を警告ダイアログで阻止する
  useEffect(() => {
    if (!loading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [loading]);

  useEffect(() => {
    if (isInitialized === false) {
      isInitialized = true;

      // 共通エラーハンドリング
      webClient.interceptors.response.use(
        (response: AxiosResponse<any, any>) => {
          // GraphQLは異常レスポンスも200 OKのため
          // レスポンスボディを見て判定する
          if (response.data.errors !== undefined) {
            return Promise.reject(response.data);
          }
          return Promise.resolve(response);
        },
        (error: any) => {
          return Promise.reject(error);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * AWS AppSyncに対してGraphQLリクエストを送信します
   * @param payload リクエストボディ
   * @returns
   */
  const request = async (payload: any) => {
    // idTokenが存在しない場合はリクエストを送信せずサインイン画面にリダイレクト
    const idToken = getIdToken();
    if (!idToken) {
      dispatch(setMessage({
        message: i18n.t('common.api.authError'),
        type: 'error' as MessageType
      }));
      dispatch(setUnauthorized());
      navigate('/signin');
      return;
    }

    const reqId = uuid();
    requestMapRef.current.set(reqId, payload);
    updateLoading();
    try {
      const result = await webClient.post(
        globalConfig.appSyncUrl,
        payload,
        {
          headers: {
            Authorization: idToken
          }
        }
      );
      return result.data;
    } catch (error) {
      // メンテナンス中の場合は画面をリロードしてメンテナンス画面を表示
      if (Axios.isAxiosError(error) && error.response && error.response.status === 503) {
        window.location.reload();
        return;
      }
      // 認証エラーの場合は、IDトークンを取り直す
      if (Axios.isAxiosError(error) && error.response && error.response.status === 401) {
        try {
          await refreshToken();
        } catch (refreshTokenError) {
          dispatch(setMessage({
            message: i18n.t('common.api.authError'),
            type: 'error' as MessageType
          }));
          await dispatch(signOut());
          // 認証情報をリセットする
          dispatch(setUnauthorized());
          navigate('/signin');
          throw refreshTokenError;
        }
        // 認証トークンを更新したらリクエストしなおす
        try {
          const result = await webClient.post(
            globalConfig.appSyncUrl,
            payload,
            {
              headers: {
                Authorization: getIdToken()
              }
            }
          );
          return result.data;
        } catch (retryError) {
          console.error(retryError);
          if (Axios.isAxiosError(retryError) && retryError.response && retryError.response.status === 401) {
            try {
              await refreshToken();
            } catch (refreshTokenError) {
              dispatch(setMessage({
                message: i18n.t('common.api.authError'),
                type: 'error' as MessageType
              }));
              await dispatch(signOut());
              // 認証情報をリセットする
              dispatch(setUnauthorized());
              navigate('/signin');
              throw refreshTokenError;
            }
          }
          dispatch(setMessage({
            message: i18n.t('common.api.processingFailed'),
            detail: getSafeErrorDetail(retryError),
            type: 'error' as MessageType
          }));
          throw retryError;
        }
      }
      console.error(error);
      dispatch(setMessage({
        message: i18n.t('common.api.processingFailed'),
        detail: getSafeErrorDetail(error),
        type: 'error' as MessageType
      }));
      throw error;
    } finally {
      requestMapRef.current.delete(reqId);
      updateLoading();
    }
  };

  /**
   * AWS AppSyncに対してGraphQLリクエストを送信します(ローディング表示、エラー表示なし)
   * @param payload リクエストボディ
   * @returns
   */
  const requestBackground = async (payload: any) => {
    // idTokenが存在しない場合はリクエストを送信せずサインイン画面にリダイレクト
    const idToken = getIdToken();
    if (!idToken) {
      dispatch(setMessage({
        message: i18n.t('common.api.authError'),
        type: 'error' as MessageType
      }));
      dispatch(setUnauthorized());
      navigate('/signin');
      return;
    }

    try {
      const result = await webClient.post(
        globalConfig.appSyncUrl,
        payload,
        {
          headers: {
            Authorization: idToken
          }
        }
      );
      return result.data;
    } catch (error) {
      // 認証エラーの場合は、IDトークンを取り直す
      if (Axios.isAxiosError(error) && error.response && error.response.status === 401) {
        try {
          await refreshToken();
        } catch (refreshTokenError) {
          dispatch(setMessage({
            message: i18n.t('common.api.authError'),
            type: 'error' as MessageType
          }));
          await dispatch(signOut());
          dispatch(setUnauthorized());
          navigate('/signin');
          throw refreshTokenError;
        }
        // 認証トークンを更新したらリクエストしなおす
        try {
          const result = await webClient.post(
            globalConfig.appSyncUrl,
            payload,
            {
              headers: {
                Authorization: getIdToken()
              }
            }
          );
          return result.data;
        } catch (retryError) {
          console.error(retryError);
          if (Axios.isAxiosError(retryError) && retryError.response && retryError.response.status === 401) {
            try {
              await refreshToken();
            } catch (refreshTokenError) {
              dispatch(setMessage({
                message: i18n.t('common.api.authError'),
                type: 'error' as MessageType
              }));
              await dispatch(signOut());
              dispatch(setUnauthorized());
              navigate('/signin');
              throw refreshTokenError;
            }
          }
          throw retryError;
        }
      }
      console.error(error);
      throw error;
    }
  };

  return (
    <WebAPIContext.Provider value={{ request, requestBackground, loading, setLoading } as WebAPIContextSchema}>
      {children}
    </WebAPIContext.Provider>
  );
};
